/**
 * setupClient — the ONLY client the first-run setup wizard (Phase E) is
 * allowed to talk to the init/TOTP/recovery/webauthn endpoints through.
 *
 * Why not the generated admin client (`../../../gen/client/react`)? That
 * client authenticates every request with `Authorization: Bearer <token>`
 * (see `useAdminGenClient`/`api/http.ts`) — but during setup there IS no
 * admin session yet: the bootstrap admin account, its first phishing-
 * resistant factor, and its recovery key are exactly what this wizard is
 * creating. The init/TOTP/recovery endpoints are instead gated by the
 * one-time setup token proven via the `X-Setup-Token` header (see
 * `setup_token.go`'s `extractSetupToken`/`requireSetupToken` on the server —
 * every failure mode there collapses to the same generic error, so this
 * client does not attempt to distinguish "no token" from "wrong token"
 * either; it simply surfaces whatever message the server returns).
 *
 * CSRF: the setup endpoints are state-changing (POST) and same-origin, so
 * they still need the double-submit CSRF defense the rest of the admin UI
 * uses (mirrors the cookie-read + 403-retry-with-refresh in
 * `gen/client/react/client.ts` and documented for the token-aware client in
 * `api/http.ts`) — setup-token possession alone does not exempt a request
 * from CSRF protection. The 403-retry is scoped to CSRF failures ONLY
 * (discriminated on the response body — see `isCsrfFailureBody`): a non-CSRF
 * 403 is a deterministic denial and is surfaced unchanged, because blindly
 * retrying it would re-POST a consumed one-shot ceremony session and mask the
 * real cause with "session not found".
 */

const CSRF_COOKIE_NAME = 'csrf_token'
const CSRF_HEADER_NAME = 'X-CSRF-Token'
const SETUP_TOKEN_HEADER_NAME = 'X-Setup-Token'
const CSRF_ENDPOINT = '/csrf'

/** Reports whether a method requires a CSRF token (mirrors the server's unsafe-method set). */
function isCsrfRequired(method: string): boolean {
  const m = method.toUpperCase()
  return m !== 'GET' && m !== 'HEAD' && m !== 'OPTIONS' && m !== 'TRACE'
}

/** Reads the CSRF token from the non-HttpOnly cookie set by the server. */
function readCsrfCookie(): string | null {
  if (typeof document === 'undefined') return null
  const match = document.cookie.match(
    new RegExp(`(?:^|; )${CSRF_COOKIE_NAME}=([^;]*)`)
  )
  return match ? decodeURIComponent(match[1] as string) : null
}

/** Extracts the human-readable error message from a (possibly JSON) body. */
function extractErrorMessage(text: string, status: number): string {
  let message = `Request failed (${status})`
  if (text) {
    try {
      const parsed = JSON.parse(text) as { error?: string; message?: string }
      message = parsed.error ?? parsed.message ?? message
    } catch {
      message = text
    }
  }
  return message
}

/**
 * Reports whether a 403 response body identifies a CSRF failure (stale or
 * missing double-submit token) — the ONLY 403 that is safe to retry after
 * refreshing the token. Any other 403 (e.g. an AAGUID/attestation policy
 * rejection) is a deterministic denial: re-POSTing would re-consume the
 * one-shot ceremony `session_id` and mask the real cause with "session not
 * found". The server tags CSRF failures with a `csrf`-bearing error message
 * (see the CSRF double-submit verifier); discriminate on that.
 */
function isCsrfFailureBody(text: string): boolean {
  if (!text) return false
  let message = text
  try {
    const parsed = JSON.parse(text) as { error?: string; message?: string }
    message = parsed.error ?? parsed.message ?? text
  } catch {
    // Non-JSON body: match against the raw text.
  }
  return /csrf/i.test(message)
}

export interface InitStatusResp {
  initialized: boolean
  steps: string[]
  // Optional only for rolling upgrades from servers predating this field.
  // Callers must treat absence as interactive setup, never as permission to
  // bypass the first-run gate.
  setup_token_enabled?: boolean
}

export interface CreateAdminResp {
  ok: boolean
  admin_id: string
}

export interface TotpEnrollBeginResp {
  otpauth_uri: string
  secret: string
}

export interface TotpEnrollVerifyResp {
  verified: boolean
}

export interface WebauthnRegisterBeginResp {
  session_id: string
  options: unknown
}

export interface WebauthnRegisterCompleteReq {
  sessionId: string
  credential: unknown
}

export interface WebauthnRegisterCompleteResp {
  credential_id: string
  transports?: string[]
  aaguid?: string
}

export interface RecoveryGenerateResp {
  recovery_key: string
}

export interface CompleteResp {
  ok: boolean
}

export interface SetupClient {
  /** Presents the in-memory setup token on every subsequent request. Never persisted (no localStorage/sessionStorage) — the token lives only for this page load. */
  setSetupToken(token: string): void
  /** PUBLIC — no setup token required. Lets the app decide whether to route to /setup at all. */
  getInitStatus(): Promise<InitStatusResp>
  /** Proves possession of the operator-supplied setup token and captures it for every subsequent call. */
  begin(token: string): Promise<InitStatusResp>
  /**
   * Creates the bootstrap admin account from the operator-entered
   * credentials, sent as the request body `{ email, password, name }`. The
   * server validates the password against its runtime PasswordPolicy and
   * hashes it before storage — the plaintext is transmitted only once, over
   * this call (TLS-protected), and never logged (see init_server.go's
   * initCreateAdmin / createAdminFromBody). When email/password are omitted
   * (empty strings), the server falls back to its THREADID_BOOTSTRAP_ADMIN_*
   * env-var configuration instead — this client always sends the fields the
   * caller provided, so an intentionally empty call still exercises that
   * fallback path.
   */
  createAdmin(
    email: string,
    password: string,
    name: string
  ): Promise<CreateAdminResp>
  totpEnrollBegin(): Promise<TotpEnrollBeginResp>
  totpEnrollVerify(code: string): Promise<TotpEnrollVerifyResp>
  webauthnRegisterBegin(): Promise<WebauthnRegisterBeginResp>
  webauthnRegisterComplete(
    req: WebauthnRegisterCompleteReq
  ): Promise<WebauthnRegisterCompleteResp>
  recoveryGenerate(): Promise<RecoveryGenerateResp>
  /** Finalizes setup. Only a SUCCESSFUL call consumes (single-use) the setup token server-side. */
  complete(): Promise<CompleteResp>
}

/**
 * Builds a setup client bound to `window.location.origin`. Every call to
 * `createSetupClient()` gets its OWN token holder (unlike the module-level
 * singleton in `api/http.ts`) since the setup wizard is a short-lived,
 * single-page flow — there is no cross-component token sharing to support.
 */
export function createSetupClient(): SetupClient {
  const baseUrl = window.location.origin
  let setupToken: string | null = null

  async function refreshCsrfToken(): Promise<string | null> {
    try {
      const res = await fetch(`${baseUrl}${CSRF_ENDPOINT}`, {
        method: 'GET',
        credentials: 'same-origin',
      })
      if (!res.ok) return null
      const body = (await res.json().catch(() => ({}))) as { token?: string }
      return body.token ?? readCsrfCookie()
    } catch {
      return null
    }
  }

  async function send<T>(
    method: string,
    path: string,
    body?: unknown
  ): Promise<T> {
    const headers = new Headers({ Accept: 'application/json' })
    if (body !== undefined) {
      headers.set('Content-Type', 'application/json')
    }
    if (setupToken) {
      headers.set(SETUP_TOKEN_HEADER_NAME, setupToken)
    }
    if (isCsrfRequired(method)) {
      const tok = readCsrfCookie()
      if (tok) headers.set(CSRF_HEADER_NAME, tok)
    }

    const requestInit: RequestInit = {
      method,
      headers,
      body: body === undefined ? null : JSON.stringify(body),
      credentials: 'same-origin',
    }

    let response = await fetch(`${baseUrl}${path}`, requestInit)

    if (response.status === 403 && isCsrfRequired(method)) {
      // Read the 403 body ONCE to classify it. Only a CSRF failure (stale
      // double-submit token) is retried after refreshing the token; any other
      // 403 is surfaced unchanged — retrying it would re-POST the same one-shot
      // ceremony session and replace the real cause (e.g. an AAGUID rejection)
      // with "session not found".
      const bodyText = await response.text().catch(() => '')
      if (isCsrfFailureBody(bodyText)) {
        const fresh = await refreshCsrfToken()
        if (fresh) {
          headers.set(CSRF_HEADER_NAME, fresh)
          response = await fetch(`${baseUrl}${path}`, {
            ...requestInit,
            headers,
          })
        } else {
          throw new Error(extractErrorMessage(bodyText, response.status))
        }
      } else {
        throw new Error(extractErrorMessage(bodyText, response.status))
      }
    }

    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new Error(extractErrorMessage(text, response.status))
    }

    const text = await response.text()
    return (text ? JSON.parse(text) : null) as T
  }

  return {
    setSetupToken(token: string): void {
      setupToken = token
    },
    getInitStatus(): Promise<InitStatusResp> {
      return send<InitStatusResp>('GET', '/v1/init/status')
    },
    begin(token: string): Promise<InitStatusResp> {
      setupToken = token
      return send<InitStatusResp>('POST', '/v1/init/begin', {})
    },
    createAdmin(
      email: string,
      password: string,
      name: string
    ): Promise<CreateAdminResp> {
      return send<CreateAdminResp>('POST', '/v1/init/admin', {
        email,
        password,
        name,
      })
    },
    totpEnrollBegin(): Promise<TotpEnrollBeginResp> {
      return send<TotpEnrollBeginResp>(
        'POST',
        '/v1/oidc/mfa/totp/enroll/begin',
        {}
      )
    },
    totpEnrollVerify(code: string): Promise<TotpEnrollVerifyResp> {
      return send<TotpEnrollVerifyResp>(
        'POST',
        '/v1/oidc/mfa/totp/enroll/verify',
        { code }
      )
    },
    webauthnRegisterBegin(): Promise<WebauthnRegisterBeginResp> {
      // The generated WebAuthnRegisterBeginReq schema requires a non-empty
      // user_id, but the server IGNORES this field for identity: it hard-binds
      // the credential's user handle to the AUTHENTICATED subject (the setup
      // token's InitState.AdminSub during bootstrap — see server.go's AuthJWT and
      // handlers_gen.go's owner-pin, M-2/CWE-840). Send a fixed schema-satisfying
      // placeholder so the body validates; the value can never influence which
      // account the ceremony binds to.
      return send<WebauthnRegisterBeginResp>(
        'POST',
        '/v1/oidc/webauthn/register/begin',
        { user_id: 'setup-admin' }
      )
    },
    webauthnRegisterComplete(
      req: WebauthnRegisterCompleteReq
    ): Promise<WebauthnRegisterCompleteResp> {
      // The generated WebAuthnRegisterCompleteReq schema requires the ceremony
      // session id on the wire as snake_case `session_id` and rejects unknown
      // members (json.RejectUnknownMembers). Map the client's camelCase field
      // explicitly — the interface keeps camelCase for ergonomics, but the wire
      // body must be snake_case or the server 400s once auth succeeds.
      return send<WebauthnRegisterCompleteResp>(
        'POST',
        '/v1/oidc/webauthn/register/complete',
        { session_id: req.sessionId, credential: req.credential }
      )
    },
    recoveryGenerate(): Promise<RecoveryGenerateResp> {
      return send<RecoveryGenerateResp>(
        'POST',
        '/v1/oidc/recovery-key/generate',
        {}
      )
    },
    complete(): Promise<CompleteResp> {
      return send<CompleteResp>('POST', '/v1/init/complete', {})
    },
  }
}
